import { ReactElement, useMemo } from 'react';
import { Alert, Stack } from '@mui/material';
import { floor, round } from 'lodash';

import { AdminReportUserSatisfactionFilters } from '~services/admin/report/types';
import { toAbsoluteUrl } from '~utils/assetsHelpers';
import LoadingFallback from '~components/LoadingFallback';
import SatisfactionRateInfo from '~components/SatisfactionRateInfo';
import DissatisfiedReviewsCard from '~components/DissatisfiedReviewsCard';
import FeedbackReviewsDownloader from '~components/FeedbackReviewsDownloader';

import { useReportsUserSatifactionSectionContext } from '../Context';

import { getPayloadGetReportUserSatisfactionData } from '../utils';

const ReportsDownloadUserSatifactionContent = (): ReactElement => {
  const {
    filters: {
      currentPeriod: { value: currentPeriod },
      currentDriverId: { value: currentDriverId },
      currentAssociatedCompanyId: { value: currentAssociatedCompanyId },
    },
    data: {
      satisfaction: {
        data: userSatisfactionData,
        loading: loadingUserSatisfaction,
      },
    },
  } = useReportsUserSatifactionSectionContext();

  const hasEnoughSurveys = useMemo(
    () => userSatisfactionData && userSatisfactionData.surveyCount >= 5,
    [userSatisfactionData],
  );

  const feedbackReviewsDownloaderFilters =
    useMemo<AdminReportUserSatisfactionFilters>(
      () =>
        getPayloadGetReportUserSatisfactionData({
          currentPeriod,
          currentDriverId,
          currentAssociatedCompanyId,
        }),
      [currentPeriod, currentDriverId, currentAssociatedCompanyId],
    );

  return (
    <>
      {loadingUserSatisfaction && <LoadingFallback />}

      {!loadingUserSatisfaction && userSatisfactionData && (
        <>
          {!hasEnoughSurveys && (
            <Alert severity="info" variant="standard" icon={false}>
              Recuerde que tiene que tener un mínimo de 5 reseñas para poder
              visualizar las estadísticas.
            </Alert>
          )}

          <SatisfactionRateInfo
            rate={floor(userSatisfactionData.satisfactionRate, 1)}
            trips={userSatisfactionData.routeItemCount}
            reviews={userSatisfactionData.surveyCount}
          />

          {hasEnoughSurveys && (
            <Stack direction="row" spacing={2}>
              <DissatisfiedReviewsCard
                value={round(userSatisfactionData.deliveryDelay, 2)}
                title="Demora en entregas"
                icon={{
                  src: toAbsoluteUrl('/icons/icon_clock.svg'),
                  alt: 'Time Icon',
                }}
                // eslint-disable-next-line max-len
                description="Experiencia de sus usuarios al momento de percibir la demora en la recepción / despacho de su pedido."
              />

              <DissatisfiedReviewsCard
                value={round(userSatisfactionData.packagingQuality, 2)}
                title="Calidad de embalaje"
                icon={{
                  src: toAbsoluteUrl('/icons/icon_box.svg'),
                  alt: 'Stop Icon',
                }}
                // eslint-disable-next-line max-len
                description="Grado de aceptación respecto a la calidad y el tratamiento del embalaje de su pedido."
              />

              <DissatisfiedReviewsCard
                value={round(userSatisfactionData.attentionReceived, 2)}
                title="Atención recibida"
                icon={{
                  src: toAbsoluteUrl('/icons/icon_truck.svg'),
                  alt: 'Vehicle Icon',
                }}
                // eslint-disable-next-line max-len
                description="Percepción de sus usuarios respecto a la atención recibida por parte del repartidor."
              />
            </Stack>
          )}

          {userSatisfactionData.surveyCount > 0 && (
            <FeedbackReviewsDownloader
              filters={feedbackReviewsDownloaderFilters}
            />
          )}
        </>
      )}
    </>
  );
};

export default ReportsDownloadUserSatifactionContent;
